import { Alert, Box, Snackbar, Stack, Typography } from "@mui/material";
import { useState } from "react";

import { useToast } from "../shared utils/useToast";
import useCampuses from "./hooks/useCampuses";
import useActivityGroupsByDate from "./hooks/useActivityGroupsByDate";
import CampusActivitiesBoard from "./jsx/boards/CampusActivitiesBoard";

export default function ActivitiesPage({
  selectedDate,
  setSelectedDate,
  selectedCampusId,
  setSelectedCampusId,
  initialSlot = "EVENING",
  onOpenStaffSchedule,
}) {
  const { showToast, toastProps } = useToast();
  const { campuses = [], loading: campusesLoading } = useCampuses();

  const selectedCampusName =
    campuses.find((campus) => String(campus.id) === String(selectedCampusId))?.name || "";

  // Only a concrete campus narrows the fetch; "All" ("") and "UNASSIGNED" still
  // pull everything and rely on client-side filtering.
  const focusCampus =
    selectedCampusName && selectedCampusName !== "UNASSIGNED" ? selectedCampusName : undefined;

  const {
    groups = [],
    loading,
    error,
    refetch,
  } = useActivityGroupsByDate(selectedDate, focusCampus);

  // Seeded once on mount from initialSlot (the app-level activities slot).
  // The board normalises this to a slot that's valid for the day, so we don't
  // re-force initialSlot here — that would override the board's correction and
  // can leave the Slot select blank (e.g. an "AFTERNOON" slot on a weekend).
  const [selectedSlot, setSelectedSlot] = useState(initialSlot);

  const handleSetSelectedCampusName = (campusName) => {
    const matchedCampus = campuses.find((campus) => campus.name === campusName);
    setSelectedCampusId(matchedCampus ? String(matchedCampus.id) : "");
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack>
          <Typography variant="h5" fontWeight={800}>
            Activities Plan
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {selectedDate.format("dddd D MMM YYYY")}
          </Typography>
        </Stack>

        {onOpenStaffSchedule && (
          <Typography
            variant="body2"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => onOpenStaffSchedule(selectedDate, selectedCampusId)}
          >
            Open Staff Scheduling →
          </Typography>
        )}
      </Stack>

      <CampusActivitiesBoard
        showToast={showToast}
        campuses={campuses}
        campusesLoading={campusesLoading}
        groups={groups}
        loading={loading}
        error={error}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedCampusName={selectedCampusName}
        setSelectedCampusName={handleSetSelectedCampusName}
        selectedSlot={selectedSlot}
        setSelectedSlot={setSelectedSlot}
        onRefresh={() => refetch?.(selectedDate)}
      />

      <Snackbar
        {...toastProps}
        // Warnings carry a server message worth reading (e.g. "changed by
        // someone else — reloaded"); give them longer than the success blips.
        autoHideDuration={toastProps.severity === "success" ? 3000 : 8000}
      >
        <Alert onClose={toastProps.onClose} severity={toastProps.severity}>
          {toastProps.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}